import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiX, FiDownload, FiTrash2, FiLoader, FiEdit3, FiUploadCloud, FiInfo, FiChevronDown, FiChevronUp, FiExternalLink, FiCornerUpLeft, FiRotateCcw, FiImage, FiRepeat, FiCrop } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds, formatSize } from "./audioShared";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const FALLBACK_STYLES = [["auto", "Best match"], ["photo", "Realistic photo"], ["digital", "Digital art"], ["anime", "Anime"], ["3d", "3D render"], ["cartoon", "Cartoon"], ["flat2d", "2D flat illustration"],
  ["oil", "Oil painting"], ["watercolor", "Watercolor"], ["sketch", "Pencil sketch"], ["pixel", "Pixel art"], ["logo", "Logo / flat design"], ["cinematic", "Cinematic"], ["fantasy", "Fantasy"]];
// The styles that turn a picture into something else come first.
const QUICK_STYLES = ["cartoon", "flat2d", "3d", "anime", "sketch", "oil", "watercolor", "pixel", "cinematic"];
const WHOLE_IDEAS = ["Make it a cartoon", "Turn it into a 3D render", "Redraw as a 2D flat illustration", "Change the background to a beach at sunset", "Make it nighttime with city lights", "Make it look like an oil painting"];
const PART_IDEAS = ["Make the sky a dramatic orange sunset", "Change it to a red one", "Remove it", "Add a straw hat", "Turn it into a wooden table", "Fill it with green plants"];
const STRENGTHS = [["keep", "Keep my picture", "Stays very close to what is there."], ["balanced", "Balanced", "A clear change that still follows your picture."], ["reimagine", "Reimagine", "Bigger changes: your picture is only a rough guide."]];
const FORMATS = [["image/png", "PNG", "png"], ["image/jpeg", "JPG", "jpg"], ["image/webp", "WebP", "webp"]];

const inputClass = "w-full px-4 py-3 rounded-xl bg-navy border border-navy-border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light";
const labelClass = "text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3";
const pillOn = "bg-accent-light text-navy border-accent-light";
const pillOff = "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light";
const ghost = "px-3.5 py-2 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-50";

function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Downloads a result in the chosen format (PNG as it is; JPG and WebP are converted in the browser).
async function download(url, filename, type) {
  try {
    const blob = await (await fetch(url)).blob();
    if (type === "image/png") return saveBlob(filename, blob);
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (type === "image/jpeg") { context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height); }
    context.drawImage(bitmap, 0, 0);
    return canvas.toBlob((out) => (out ? saveBlob(filename, out) : window.open(url, "_blank", "noopener")), type, 0.92);
  } catch {
    return window.open(url, "_blank", "noopener");   // the browser downloads it itself
  }
}

const slug = (text) => (text || "edited").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40).replace(/^-|-$/g, "") || "edited";

// Draws brush strokes (fractions of the picture's width and height) in white: "keep" strokes add to the area, "remove" strokes rub it out.
function drawStrokes(context, list, width, height) {
  const longest = Math.max(width, height);
  context.lineCap = "round";
  context.lineJoin = "round";
  context.strokeStyle = "#fff";
  context.fillStyle = "#fff";
  list.forEach((stroke) => {
    context.globalCompositeOperation = stroke.m === "keep" ? "source-over" : "destination-out";
    const radius = Math.max(1, stroke.r * longest);
    context.lineWidth = radius * 2;
    context.beginPath();
    stroke.p.forEach(([x, y], i) => (i ? context.lineTo(x * width, y * height) : context.moveTo(x * width, y * height)));
    context.stroke();
    if (stroke.p.length === 1) { context.beginPath(); context.arc(stroke.p[0][0] * width, stroke.p[0][1] * height, radius, 0, Math.PI * 2); context.fill(); }
  });
  context.globalCompositeOperation = "source-over";
}

// The picture before and after, with a handle to drag across.
function Compare({ before, after, alt }) {
  const [position, setPosition] = useState(50);
  const boxRef = useRef(null);
  const dragging = useRef(false);
  const move = (clientX) => {
    const rect = boxRef.current.getBoundingClientRect();
    setPosition(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
  };
  return (
    <div>
      <div
        ref={boxRef}
        className="relative select-none touch-none overflow-hidden rounded-xl bg-black/30 cursor-ew-resize"
        onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); move(e.clientX); }}
        onPointerMove={(e) => dragging.current && move(e.clientX)}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
      >
        <img src={after} alt={alt} draggable={false} className="block w-full max-h-[70vh] object-contain mx-auto" />
        {before && (
          <>
            <img src={before} alt="The original, before the change" draggable={false} className="absolute inset-0 w-full h-full object-contain" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }} />
            <span className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_6px_rgba(0,0,0,0.6)] pointer-events-none" style={{ left: `${position}%` }}>
              <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-white text-navy text-xs font-bold flex items-center justify-center shadow-lg">&#8596;</span>
            </span>
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-xs text-white pointer-events-none">Before</span>
            <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 text-xs text-white pointer-events-none">After</span>
          </>
        )}
      </div>
      {before && <input type="range" min="0" max="100" value={Math.round(position)} onChange={(e) => setPosition(Number(e.target.value))} className="sr-only" aria-label="Compare before and after" />}
    </div>
  );
}

// Image to Image Editing: upload a picture and change it by writing what you want - the whole picture, or only the part you paint.
// The work runs on the server in the background and this page polls for the result.
export default function ImageEditorTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("imageedit");
  const [styles, setStyles] = useState(FALLBACK_STYLES);
  const [file, setFile] = useState(null);                  // { file, url }
  const [trail, setTrail] = useState([]);                  // earlier versions of the picture, to go back to
  const [where, setWhere] = useState("whole");             // "whole" | "part"
  const [strokes, setStrokes] = useState([]);
  const [brush, setBrush] = useState({ mode: "keep", size: 0.05 });
  const [invert, setInvert] = useState(false);
  const [hover, setHover] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("auto");
  const [strength, setStrength] = useState("balanced");
  const [count, setCount] = useState(1);
  const [negative, setNegative] = useState("");
  const [seed, setSeed] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pick, setPick] = useState(0);
  const inputRef = useRef(null);
  const resultRef = useRef(null);
  const areaRef = useRef(null);
  const overlayRef = useRef(null);
  const strokeRef = useRef(null);

  const part = where === "part";
  const shownStyles = [...QUICK_STYLES.map((k) => styles.find(([key]) => key === k)).filter(Boolean), ...styles.filter(([k]) => !QUICK_STYLES.includes(k))];
  const hasWords = prompt.trim().length >= 3 || style !== "auto";
  const needsArea = part && strokes.length === 0;
  const canSubmit = !!file && hasWords && !needsArea && !submitting && !running;

  useEffect(() => {
    let alive = true;
    apiRequest("/core/image/options/").then(({ ok, data }) => { if (alive && ok && data.styles) setStyles(data.styles.map((s) => [s.key, s.label])); });
    return () => { alive = false; };
  }, []);

  // Show the newest finished job automatically.
  useEffect(() => {
    if (latest && latest.status === "completed" && latest.images?.length) { setViewId(latest.id); setPick(0); }
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, clientId]);

  // ---------------------------------------------------------------------------------------- the picture
  const pickFile = (picked, keepTrail) => {
    setFieldError("");
    if (!picked) return;
    if (!picked.type.startsWith("image/")) return setFieldError("Choose a JPG, PNG or WebP picture.");
    if (picked.size > MAX_FILE_BYTES) return setFieldError("This picture is larger than 10 MB.");
    if (keepTrail && file) setTrail((t) => [...t.slice(-4), file]);
    else if (file?.url) URL.revokeObjectURL(file.url);
    if (!keepTrail) setTrail([]);
    setFile({ file: picked, url: URL.createObjectURL(picked) });
    setStrokes([]);
    setInvert(false);
  };

  const goBack = () => {
    const previous = trail[trail.length - 1];
    if (!previous) return;
    if (file?.url) URL.revokeObjectURL(file.url);
    setTrail((t) => t.slice(0, -1));
    setFile(previous);
    setStrokes([]);
  };

  // A finished picture becomes the picture to change next.
  const continueFrom = async (image) => {
    try {
      const blob = await (await fetch(image.url)).blob();
      pickFile(new File([blob], "edited.png", { type: blob.type || "image/png" }), true);
      setPrompt("");
      setStyle("auto");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("The picture could not be loaded again. Download it and upload it instead.");
    }
  };

  // ---------------------------------------------------------------------------------------- the brush
  const paintOverlay = useCallback(() => {
    const canvas = overlayRef.current;
    const area = areaRef.current;
    if (!canvas || !area) return;
    const rect = area.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    if (!width || !height) return;
    if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
    const live = strokeRef.current;
    const list = live ? [...strokes, live] : strokes;
    const layer = document.createElement("canvas");
    layer.width = width;
    layer.height = height;
    drawStrokes(layer.getContext("2d"), list, width, height);
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, width, height);
    context.fillStyle = "rgba(34,211,238,0.5)";
    if (invert) {                                          // everything EXCEPT the painted part is to change
      context.fillRect(0, 0, width, height);
      context.globalCompositeOperation = "destination-out";
      context.drawImage(layer, 0, 0);
    } else {
      context.drawImage(layer, 0, 0);
      context.globalCompositeOperation = "source-in";
      context.fillRect(0, 0, width, height);
    }
    context.globalCompositeOperation = "source-over";
  }, [strokes, invert]);

  useEffect(() => {
    paintOverlay();
    if (!part || !areaRef.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(paintOverlay);
    observer.observe(areaRef.current);
    return () => observer.disconnect();
  }, [paintOverlay, part, file]);

  const pointOf = (event) => {
    const rect = areaRef.current.getBoundingClientRect();
    return [Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))];
  };
  const brushDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    strokeRef.current = { m: brush.mode, r: brush.size, p: [pointOf(event)] };
    paintOverlay();
  };
  const brushMove = (event) => {
    const point = pointOf(event);
    setHover(point);
    const stroke = strokeRef.current;
    if (!stroke) return;
    const last = stroke.p[stroke.p.length - 1];
    if (Math.hypot(point[0] - last[0], point[1] - last[1]) > 0.003 && stroke.p.length < 590) { stroke.p.push(point); paintOverlay(); }
  };
  const brushUp = () => {
    const stroke = strokeRef.current;
    strokeRef.current = null;
    if (stroke) setStrokes((list) => [...list, stroke].slice(-290));
  };

  // ---------------------------------------------------------------------------------------- sending
  const submit = async (override) => {
    setError("");
    setFieldError("");
    setSubmitting(true);
    const values = { prompt, negative, style, strength, count, seed, ...override };
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "imageedit");
    form.append("prompt", values.prompt);
    form.append("negative", values.negative);
    form.append("style", values.style);
    form.append("strength", values.strength);
    form.append("count", String(values.count));
    form.append("seed", String(values.seed || ""));
    form.append("original", file.file);
    if (part && strokes.length) {
      form.append("strokes", JSON.stringify(strokes));
      form.append("invert", invert ? "true" : "false");
    }
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.prompt || data?.original || data?.strokes) return setFieldError(data.prompt || data.original || data.strokes);
      return setError(errorMessage(data));
    }
    setViewId(null);
    add(data);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const view = detail && detail.status === "completed" ? detail : null;
  const stored = (() => { try { return JSON.parse(view?.options || "{}"); } catch { return {}; } })();
  const extra = view?.segments && !Array.isArray(view.segments) ? view.segments : {};
  const styleLabel = (key) => styles.find(([k]) => k === key)?.[1] || key;
  const current = view?.images?.[Math.min(pick, (view?.images?.length || 1) - 1)];
  const runningPart = running && (() => { try { return JSON.parse(running.options || "{}").part; } catch { return false; } })();
  const ideas = part ? PART_IDEAS : WHOLE_IDEAS;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Image-to-Image Editing</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Upload a picture and write what you want changed - a new style (cartoon, 2D, 3D, sketch), a different background or time of day, an added or removed object.
        Change the whole picture, or paint just the part to change and keep everything else exactly as it is.
      </p>

      <div className="rounded-2xl bg-white/5 border border-navy-border p-6 space-y-7">
        {/* 1. the picture */}
        <div>
          <span className={labelClass}>1. Your picture</span>
          {!file ? (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
              role="button" tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-12 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
            >
              <FiUploadCloud className="mx-auto text-accent-light" size={32} />
              <p className="mt-3 text-sm text-gray-200">Click to choose a picture, or drop it here</p>
              <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP - up to 10 MB</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <p className="text-sm text-gray-300 min-w-0 truncate">{file.file.name} <span className="text-gray-500">- {formatSize(file.file.size)}</span></p>
                <div className="flex flex-wrap gap-2">
                  {trail.length > 0 && <button type="button" onClick={goBack} className={ghost}><FiCornerUpLeft size={13} /> Back to the earlier picture</button>}
                  <button type="button" onClick={() => inputRef.current?.click()} className={ghost}><FiUploadCloud size={13} /> Choose another</button>
                  <button type="button" onClick={() => { if (file?.url) URL.revokeObjectURL(file.url); setFile(null); setTrail([]); setStrokes([]); }} className={ghost} aria-label="Remove picture"><FiX size={13} /> Remove</button>
                </div>
              </div>

              <div className="grid lg:grid-cols-[1fr_16rem] gap-5 items-start">
                <div className="rounded-xl overflow-hidden bg-navy flex items-center justify-center p-2 min-h-[220px]">
                  <div
                    ref={areaRef}
                    className={`relative inline-block select-none ${part ? "touch-none cursor-none" : ""}`}
                    onPointerDown={part ? brushDown : undefined}
                    onPointerMove={part ? brushMove : undefined}
                    onPointerUp={part ? brushUp : undefined}
                    onPointerCancel={part ? brushUp : undefined}
                    onPointerLeave={() => setHover(null)}
                    role={part ? "application" : undefined}
                    aria-label={part ? "Brush area: paint over the part of the picture that should change" : undefined}
                  >
                    <img src={file.url} alt="The upload, ready to change" draggable={false} className="block max-h-[62vh] w-auto max-w-full" />
                    {part && <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none" />}
                    {part && hover && (
                      <span
                        className="absolute pointer-events-none rounded-full border-2 -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${hover[0] * 100}%`, top: `${hover[1] * 100}%`, width: `${brush.size * 200 * Math.max(areaRef.current?.clientWidth || 1, areaRef.current?.clientHeight || 1) / (areaRef.current?.clientWidth || 1)}%`, aspectRatio: "1", borderColor: brush.mode === "keep" ? "#22d3ee" : "#f87171" }}
                      />
                    )}
                  </div>
                </div>

                {/* where to change */}
                <div className="space-y-4">
                  <span className={labelClass}>Where should it change?</span>
                  {[["whole", "The whole picture", FiImage, "Restyle it, or change its scene, light or colours."], ["part", "Only the part I paint", FiCrop, "Paint over what should change. Everything else stays exactly as it is."]].map(([key, label, Icon, hint]) => (
                    <button key={key} type="button" onClick={() => setWhere(key)} aria-pressed={where === key}
                      className={`w-full text-start rounded-xl border p-3.5 transition-colors ${where === key ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}>
                      <p className="text-sm font-semibold inline-flex items-center gap-2"><Icon size={14} /> {label}</p>
                      <p className="text-xs text-gray-400 mt-1">{hint}</p>
                    </button>
                  ))}
                  {part && (
                    <div className="rounded-xl border border-navy-border p-3.5 space-y-3">
                      <div className="flex gap-2">
                        {[["keep", "Paint"], ["remove", "Rub out"]].map(([key, label]) => (
                          <button key={key} type="button" onClick={() => setBrush((b) => ({ ...b, mode: key }))} aria-pressed={brush.mode === key}
                            className={`flex-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${brush.mode === key ? pillOn : pillOff}`}>{label}</button>
                        ))}
                      </div>
                      <label className="block">
                        <span className="flex items-center justify-between text-xs text-gray-300 mb-1.5">Brush size<span className="text-gray-500 tabular-nums">{Math.round(brush.size * 100)}%</span></span>
                        <input type="range" min="1" max="20" step="1" value={Math.round(brush.size * 100)} onChange={(e) => setBrush((b) => ({ ...b, size: Number(e.target.value) / 100 }))} className="w-full accent-[#22d3ee]" aria-label="Brush size" />
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => setStrokes((s) => s.slice(0, -1))} disabled={!strokes.length} className={ghost}><FiCornerUpLeft size={13} /> Undo</button>
                        <button type="button" onClick={() => setStrokes([])} disabled={!strokes.length} className={ghost}><FiRotateCcw size={13} /> Clear</button>
                      </div>
                      <label className="flex items-start gap-2.5 text-xs text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={invert} onChange={(e) => setInvert(e.target.checked)} className="mt-0.5 accent-[#22d3ee] w-4 h-4" />
                        <span>Change everything EXCEPT the painted part<span className="block text-gray-500">Paint the person or object to keep, then change the rest.</span></span>
                      </label>
                      <p className="text-[11px] text-gray-500">The blue area is what will change.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { pickFile(e.target.files?.[0], false); e.target.value = ""; }} />
        </div>

        {/* 2. what to change */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="edit-prompt" className="text-xs uppercase tracking-widest text-white/50 font-semibold">
              2. {part ? "What should the painted part become?" : "What should change?"}
              <span className="normal-case tracking-normal text-gray-500 font-normal"> (optional if you choose a style below)</span>
            </label>
            <span className="text-xs text-gray-500">{prompt.length} / 500</span>
          </div>
          <textarea
            id="edit-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, 500))}
            placeholder={part ? "e.g. a dramatic orange sunset sky - or: remove it - or: a red leather jacket" : "e.g. Make it a cartoon with bold outlines, or: change the background to a beach at sunset"}
            dir="auto"
            className={`${inputClass} min-h-[96px] leading-relaxed text-base`}
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {ideas.map((idea) => (
              <button key={idea} type="button" onClick={() => setPrompt(idea)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light">{idea}</button>
            ))}
          </div>
          {fieldError && <p className="text-sm text-amber-300 mt-3">{fieldError}</p>}
          <p className="text-xs text-gray-500 mt-3">Write in English, Urdu, Arabic, Hindi... any language. Say what the result should look like; short, clear wording works best.</p>
        </div>

        <div>
          <span className={labelClass}>3. Style to turn it into (optional)</span>
          <div className="flex flex-wrap gap-2">
            {shownStyles.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setStyle(key)} aria-pressed={style === key}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${style === key ? pillOn : pillOff}`}>{label}</button>
            ))}
          </div>
        </div>

        <div>
          <span className={labelClass}>4. How much may change</span>
          <div className="grid sm:grid-cols-3 gap-3">
            {STRENGTHS.map(([key, label, hint]) => (
              <button key={key} type="button" onClick={() => setStrength(key)} aria-pressed={strength === key}
                className={`text-start rounded-xl border p-3.5 transition-colors ${strength === key ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}>
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-xs text-gray-400 mt-1">{hint}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <span className={labelClass}>How many versions</span>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((n) => (
                <button key={n} type="button" onClick={() => setCount(n)} aria-pressed={count === n}
                  className={`w-11 h-10 rounded-full text-sm font-semibold border transition-colors ${count === n ? pillOn : pillOff}`}>{n}</button>
              ))}
            </div>
          </div>
          <button type="button" onClick={() => setAdvanced((v) => !v)} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5">
            {advanced ? <FiChevronUp /> : <FiChevronDown />} More options
          </button>
        </div>

        {advanced && (
          <div className="grid sm:grid-cols-2 gap-4 border-t border-navy-border pt-5">
            <div>
              <label htmlFor="edit-negative" className={labelClass}>Things to avoid (optional)</label>
              <input id="edit-negative" className={inputClass} value={negative} onChange={(e) => setNegative(e.target.value.slice(0, 200))} placeholder="e.g. text, extra people, blur" dir="auto" />
            </div>
            <div>
              <label htmlFor="edit-seed" className={labelClass}>Seed (optional)</label>
              <input id="edit-seed" className={inputClass} value={seed} onChange={(e) => setSeed(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Empty = a different result every time" inputMode="numeric" />
              <p className="text-xs text-gray-500 mt-1">The same picture, words and seed give the same result again.</p>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}

      <button
        onClick={() => submit()}
        disabled={!canSubmit}
        className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Editing...</> : <><FiEdit3 /> {part ? "Change the painted part" : "Change my picture"}{count > 1 ? ` (${count} versions)` : ""}</>}
      </button>
      {!canSubmit && !running && !submitting && (
        <p className="text-xs text-gray-500 text-center mt-2">{!file ? "Add your picture first" : needsArea ? "Paint the part that should change on your picture, or choose \"The whole picture\"" : "Say what to change, or pick a style"}</p>
      )}
      <p className="text-xs text-gray-500 text-center mt-3">Free image services redraw your picture. Your picture and words are sent to them, so do not use private or confidential pictures.
        Sexual content, graphic violence and requests to undress people are refused. Pictures are edited at up to 1024 pixels on the long side.</p>

      <div ref={resultRef} />
      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div>
            <p className="font-semibold">
              {runningPart ? "Changing the painted part" : "Changing your picture"}... {formatSeconds(elapsed)}
              {running.progress && <span className="text-gray-400 font-normal"> · {running.progress}</span>}
            </p>
            <p className="text-sm text-gray-400">
              {runningPart ? "Usually a minute or two, but the free painting models can have a queue of several minutes. " : "Usually 10-40 seconds, longer when the free services are busy. "}
              You can leave this page open.
            </p>
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The picture could not be changed. Please try again."}</span>
        </div>
      )}

      {view && current && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">
                {extra.part ? "Painted part changed" : "Your picture, changed"}{stored.style && stored.style !== "auto" ? ` · ${styleLabel(stored.style)}` : ""}
              </p>
              <p className="font-semibold">{view.original_name}</p>
            </div>
            {view.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">{view.engine}</span>}
          </div>

          {extra.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4">{extra.notice}</p>}

          <Compare before={view.source_image} after={current.url} alt={`Result: ${view.original_name}`} />
          <p className="text-xs text-gray-500 mt-2">Drag across the picture to compare it with your original.</p>

          {view.images.length > 1 && (
            <div className="flex flex-wrap gap-3 mt-4">
              {view.images.map((image, i) => (
                <button key={image.url} type="button" onClick={() => setPick(i)} aria-pressed={pick === i} aria-label={`Show version ${i + 1}`}
                  className={`rounded-lg overflow-hidden border-2 ${pick === i ? "border-accent-light" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={image.thumb} alt={`Version ${i + 1}`} className="w-20 h-20 object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-5">
            {FORMATS.map(([type, label, extension]) => (
              <button key={type} type="button" onClick={() => download(current.url, `${slug(view.original_name)}-${pick + 1}.${extension}`, type)} className={ghost}><FiDownload size={13} /> {label}</button>
            ))}
            <button type="button" onClick={() => continueFrom(current)} className={ghost}><FiEdit3 size={13} /> Keep editing this picture</button>
            <button type="button" onClick={() => submit({ seed: "" })} disabled={!canSubmit} className={ghost}><FiRepeat size={13} /> Make new versions</button>
            <a href={current.url} target="_blank" rel="noreferrer" className={ghost}><FiExternalLink size={13} /> Open</a>
            {current.seed != null && <span className="text-[11px] text-gray-500 ms-auto self-center">seed {current.seed}</span>}
          </div>
          {extra.part && <p className="text-xs text-gray-400 mt-3">Only the part you painted was redrawn; the rest is your original picture.</p>}
          {view.transcript && (
            <details className="mt-4 text-xs text-gray-400">
              <summary className="cursor-pointer hover:text-white">What was asked of the image model</summary>
              <p className="mt-2 leading-relaxed" dir="auto">{view.transcript}</p>
              {extra.translated && <p className="mt-2 text-gray-500">Translated from your words: {extra.translated}</p>}
            </details>
          )}
          <p className="text-xs text-gray-500 mt-4">AI edits can contain mistakes (hands, faces, text, small details). Check the result before you use it, and do not present it as an unedited photograph.</p>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your edits</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your edited pictures will appear here.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border overflow-hidden ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                {job.status === "completed" && job.images?.length ? (
                  <button type="button" onClick={() => { setViewId(job.id); setPick(0); }} className="block w-full" aria-label={`Show ${job.original_name}`}>
                    <img src={job.images[0].thumb} alt={job.original_name} className="w-full aspect-square object-cover" loading="lazy" />
                  </button>
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center text-xs text-gray-500 px-3 text-center bg-navy">
                    {job.status === "failed" ? <span className="text-red-300">{job.error_message}</span> : <span className="inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Editing...</span>}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-gray-200 line-clamp-2 min-h-[2rem]" dir="auto">{job.original_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-gray-500">{job.images?.length > 1 ? `${job.images.length} versions` : new Date(job.created_at).toLocaleDateString()}</span>
                    {job.status !== "processing" && job.status !== "queued" && (
                      <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
